<script>
import { ref, onBeforeMount, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useRestoGLobalStore } from '@/stores/restoGlobalStore'
import { useFeatureFlagsStore } from '@/stores/featureFlagsStore'
import { useAuthGlobalStore } from '@/stores/authGlobalStore'
import { getRestoPaymentSettingFn } from '@/services/api/restApiCalls'
import StripePaymentElement from './StripePaymentElement.vue'
import StripeCardPayment from './StripeCardPayment.vue'
import MolliePaymentV1 from './MolliePaymentV1.vue'
import MolliePaymentV2 from './MolliePaymentV2.vue'

export default {
  components: {
    StripePaymentElement,
    StripeCardPayment,
    MolliePaymentV1,
    MolliePaymentV2
  },

  props: {
    beforePlaceOrder: {
      type: Function,
      default: null
    }
  },

  setup(props, { expose }) {
    const router = useRouter()
    const restoStore = useRestoGLobalStore()
    const featureFlagsStore = useFeatureFlagsStore()
    const authStore = useAuthGlobalStore()

    const paymentGateway = ref('stripe')
    const mollieProfileId = ref('')
    const mollieComponentVersion = ref('v1')
    const mollieTestmode = ref(false)
    const mollieIsConnected = ref(false)
    const mollieCanReceivePayments = ref(false)
    const mollieOnboardingStatus = ref('')
    const mollieConnectionError = ref('')
    const isLoaded = ref(false)
    const childRef = ref(null)

    const showStripePaymentElement = computed(() => {
      return (
        paymentGateway.value === 'stripe' &&
        featureFlagsStore.isApplePayEnabled(restoStore?.RESTO_ID, authStore?.user?.customer_id)
      )
    })

    const showMollieV1 = computed(() => {
      return (
        paymentGateway.value === 'mollie' &&
        !mollieConnectionError.value &&
        mollieComponentVersion.value !== 'v2'
      )
    })

    const showMollieV2 = computed(() => {
      return (
        paymentGateway.value === 'mollie' &&
        !mollieConnectionError.value &&
        mollieComponentVersion.value === 'v2'
      )
    })

    const payButtonDisabled = computed(() => {
      if (mollieConnectionError.value) return true
      return childRef.value?.payButtonDisabled !== false
    })

    const handleStripePayment = () => {
      childRef.value?.handleStripePayment?.()
    }

    onBeforeMount(async () => {
      let settings = await getRestoPaymentSettingFn(restoStore?.RESTO_ID)
      if (!settings?.gateway) {
        settings = await getRestoPaymentSettingFn(restoStore?.RESTO_ID)
      }

      paymentGateway.value = settings?.gateway || 'stripe'
      mollieProfileId.value = settings?.mollie_profile_id || ''
      mollieComponentVersion.value = settings?.mollie_component_version || 'v1'
      mollieTestmode.value = !!settings?.mollie_testmode
      mollieIsConnected.value = !!settings?.mollie_is_connected
      mollieCanReceivePayments.value = !!settings?.mollie_can_receive_payments
      mollieOnboardingStatus.value = settings?.mollie_onboarding_status || ''

      if (paymentGateway.value === 'mollie') {
        if (!mollieIsConnected.value) {
          mollieConnectionError.value =
            'Card payments are not available — this restaurant has not connected Mollie yet.'
        } else if (!mollieCanReceivePayments.value) {
          mollieConnectionError.value =
            'Card payments are temporarily unavailable while Mollie onboarding completes.'
          if (mollieOnboardingStatus.value) {
            mollieConnectionError.value += ` Status: ${mollieOnboardingStatus.value}.`
          }
        } else if (!mollieProfileId.value && mollieComponentVersion.value !== 'v2') {
          mollieConnectionError.value =
            'Card payments are not configured — no Mollie profile is selected for this restaurant.'
        }
      }

      isLoaded.value = true
    })

    expose({
      handleStripePayment,
      payButtonDisabled
    })

    return {
      isLoaded,
      paymentGateway,
      mollieProfileId,
      mollieComponentVersion,
      mollieTestmode,
      mollieConnectionError,
      showStripePaymentElement,
      showMollieV1,
      showMollieV2,
      childRef,
      beforePlaceOrder: props.beforePlaceOrder,
      handleStripePayment,
      payButtonDisabled
    }
  }
}
</script>

<template>
  <div v-if="isLoaded" class="card-payment-gateway">
    <div
      v-if="paymentGateway === 'mollie' && mollieConnectionError"
      class="p-4 text-center text-red-600 dark:text-red-400 text-sm font-medium"
    >
      {{ mollieConnectionError }}
    </div>
    <StripePaymentElement
      v-else-if="showStripePaymentElement"
      ref="childRef"
      :before-place-order="beforePlaceOrder"
    />
    <StripeCardPayment
      v-else-if="paymentGateway === 'stripe'"
      ref="childRef"
      :before-place-order="beforePlaceOrder"
    />
    <MolliePaymentV2
      v-else-if="showMollieV2"
      ref="childRef"
      :before-place-order="beforePlaceOrder"
    />
    <MolliePaymentV1
      v-else-if="showMollieV1"
      ref="childRef"
      :profile-id="mollieProfileId"
      :testmode="mollieTestmode"
      :before-place-order="beforePlaceOrder"
    />
  </div>
  <div v-else class="p-4 text-center text-gray-500 dark:text-gray-400">
    Loading payment details...
  </div>
</template>
